// Resúmenes de contenido para la tarjeta del nodo: permiten reconocer un nodo a
// simple vista (qué dice, qué evalúa, a dónde llama) sin tener que abrirlo.
import { isStickerUrl } from './stickerMessage';

// {{vo9LoGIok0-webHook.serviceStatus}} -> serviceStatus. Las variables de un
// nodo llevan su id delante; en la tarjeta solo aporta ruido.
const NODE_VARIABLE_PREFIX = /^[A-Za-z0-9]{10}-[A-Za-z]+\./;

export const shortVariable = (raw) =>
  String(raw ?? '')
    .replace(/^\s*\{\{\s*|\s*\}\}\s*$/g, '')
    .trim()
    .replace(NODE_VARIABLE_PREFIX, '');

// Formato de WhatsApp (*negrita*, _cursiva_, ~tachado~) solo cuando envuelve
// palabras, para no romper variables como {{nombre_usuario}}.
const WHATSAPP_FORMAT = /(^|[\s(])([*_~])([^\s*_~](?:[^\n]*?[^\s])?)\2(?=$|[\s).,:;!?])/g;

export const summarizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(WHATSAPP_FORMAT, '$1$3')
    .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, variable) => `{${shortVariable(variable)}}`)
    .replace(/\s+/g, ' ')
    .trim();
};

const OPERATOR_SYMBOLS = {
  equal_to: '=',
  equal_to_case_sensative: '=',
  not_equal_to: '≠',
  not_equal_to_case_sensative: '≠',
  greater_than: '>',
  less_than: '<',
  greater_than_or_equal_to: '≥',
  less_than_or_equal_to: '≤',
};

export const describeRuleParts = (rule) => {
  if (!rule) return null;
  const operator = rule.operator || {};
  const label = operator.label || '';
  const variable = rule.value1 ? shortVariable(rule.value1) : '';

  if (!variable) {
    const text = operator.type === 'whatsapp_message' && label ? `Mensaje: ${label}` : label;
    return text ? { variable: '', operator: '', value: '', text } : null;
  }
  if (operator.id === 'exist' || operator.id === 'does_not_exist') {
    const word = operator.id === 'exist' ? 'existe' : 'no existe';
    return { variable, operator: word, value: '', text: `${variable} ${word}` };
  }

  const symbol = OPERATOR_SYMBOLS[operator.id] || label.toLowerCase();
  const rawValue = rule.value2 === undefined || rule.value2 === null ? '' : String(rule.value2);
  const value = rawValue === '' ? '""' : summarizeText(rawValue);
  return { variable, operator: symbol, value, text: `${variable} ${symbol} ${value}` };
};

const describeRule = (rule) => describeRuleParts(rule)?.text || '';

export const summarizeConditions = (conditions) => {
  const rules = (Array.isArray(conditions) ? conditions : [])
    .map((condition) => describeRule(condition?.rule))
    .filter(Boolean);
  if (!rules.length) return '';
  return rules.length === 1 ? rules[0] : `${rules[0]} (+${rules.length - 1})`;
};

// POST https://api.taxicloud.co/services/cancel/{{id-queryApi.request_id}}
//   -> POST /services/cancel/{request_id}
export const splitRequest = (protocol, url) => {
  const method = protocol?.label || protocol?.id || '';
  if (!url) return { method, target: '' };
  const readable = summarizeText(String(url));
  const withoutHost = readable.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const target = withoutHost && withoutHost !== '/'
    ? withoutHost
    : readable.replace(/^[a-z]+:\/\//i, '').replace(/\/$/, '');
  return { method, target };
};

export const summarizeRequest = (protocol, url) => {
  const { method, target } = splitRequest(protocol, url);
  return [method, target].filter(Boolean).join(' ');
};

// Nombres de las variables donde guarda un nodo "Guardar respuesta".
export const getSavedVariables = (variable) => {
  const list = Array.isArray(variable) ? variable : variable ? [variable] : [];
  return list.map((v) => shortVariable(v?.fieldCorresp)).filter(Boolean);
};

// '{"status":"ok","items":[]}' -> '{ status, items }'
export const summarizeJson = (json) => {
  if (json === undefined || json === null || json === '') return '';
  let value = json;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      return summarizeText(value);
    }
  }
  if (value && typeof value === 'object') {
    const keys = Object.keys(value);
    if (!keys.length) return '{ }';
    return `{ ${keys.slice(0, 4).join(', ')}${keys.length > 4 ? ', …' : ''} }`;
  }
  return String(value);
};

// "vehiculo_no_disponible (#1957360134906960)" -> "vehiculo_no_disponible"
export const cleanTemplateName = (name) =>
  String(name || '').replace(/\s*\(#\d+\)\s*$/, '').trim();

// Títulos que el constructor pone por defecto. Si el título es uno de estos,
// la tarjeta muestra el resumen como texto principal; si el usuario lo
// personalizó, se respeta y el resumen pasa a segunda línea.
const GENERIC_TITLES = new Set(
  [
    'Respuesta Básica',
    'Respues Básica',
    'Condiciones',
    'Guardar Respuesta',
    'Consulta API',
    'API Request',
    'WebHook',
    'Enviar Plantilla',
    'Plantilla',
    'Etiquetar',
    'Etiquetas',
    'Transferir Conversación',
    'Transferir Agente',
    'Asistente IA',
    'JSON',
    'Flujo de WhatsApp',
    'Flujo WhatsApp',
  ].map((title) => normalizeTitle(title))
);

function normalizeTitle(title) {
  return String(title || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export const isGenericTitle = (title) => !title || GENERIC_TITLES.has(normalizeTitle(title));

// Mismo resumen que muestra la tarjeta, calculado desde el nodo completo. Lo usa
// el buscador para encontrar y listar los nodos por su contenido.
// (Flujo WhatsApp no aparece: su nombre sale de una consulta a la API.)
export const getNodeSummary = (node) => {
  const data = node?.data || {};
  switch (node?.type) {
    case 'basicAnswer':
      // Una respuesta puede ser solo un archivo (imagen, PDF...) sin texto.
      return summarizeText(data.bot_response) || (data.file?.name ? `📎 ${data.file.name}` : '');
    case 'json':
      return summarizeJson(data.json);
    case 'conditions':
      return summarizeConditions(data.conditions);
    case 'queryApi':
      return summarizeRequest(data.protocol, data.url_api);
    case 'template':
      return cleanTemplateName(data.template_name);
    case 'saveAnswer': {
      const names = getSavedVariables(data.variable);
      return names.length ? `→ ${names.join(', ')}` : '';
    }
    case 'tags':
      return (data.tags || []).map((tag) => tag?.title).filter(Boolean).join(', ');
    case 'transferAgent':
      return data.agent_area?.label || '';
    case 'assistantAI':
      return data.assistant?.name || '';
    case 'webHook':
      return data.status_format_property
        ? `Espera evento · ${data.status_format_property}`
        : 'Espera evento';
    default:
      return '';
  }
};

// Texto principal de la tarjeta: el título si el usuario lo personalizó; si no,
// el resumen del contenido.
export const getNodeHeadline = (node) => {
  const title = node?.data?.title;
  return isGenericTitle(title) ? getNodeSummary(node) || title || '' : title;
};

// ---------------------------------------------------------------------------
// Contenido para la tarjeta del canvas. A diferencia de los resúmenes de arriba
// (texto plano, para el buscador), conserva las variables {{...}} completas para
// pintarlas como chips con el color del nodo de origen, igual que en el editor.
// ---------------------------------------------------------------------------

// Quita el formato de WhatsApp y los saltos de línea, sin tocar las variables.
export const cleanMessage = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text.replace(WHATSAPP_FORMAT, '$1$3').replace(/\s+/g, ' ').trim();
};

const withBraces = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  return /^\{\{.*\}\}$/.test(text) ? text : `{{${text}}}`;
};

const OPERATOR_WORDS = {
  equal_to: 'es igual a',
  equal_to_case_sensative: 'es igual a',
  not_equal_to: 'no es igual a',
  not_equal_to_case_sensative: 'no es igual a',
  greater_than: 'es mayor que',
  less_than: 'es menor que',
  greater_than_or_equal_to: 'es mayor o igual a',
  less_than_or_equal_to: 'es menor o igual a',
};

// "{{x-webHook.serviceStatus}} es igual a TRAVELLING"
const ruleSentence = (rule) => {
  if (!rule) return '';
  const operator = rule.operator || {};
  const label = String(operator.label || '').trim();
  const variable = rule.value1 ? String(rule.value1).trim() : '';

  // Sin variable: estado del contacto ("solicitud de nombre") o tipo de mensaje.
  if (!variable) {
    if (!label) return '';
    return operator.type === 'whatsapp_message'
      ? `el mensaje es ${label.toLowerCase()}`
      : label.toLowerCase();
  }
  if (operator.id === 'exist') return `${variable} existe`;
  if (operator.id === 'does_not_exist') return `${variable} no existe`;

  const word = OPERATOR_WORDS[operator.id] || label.toLowerCase();
  const value = rule.value2 === undefined || rule.value2 === null ? '' : String(rule.value2).trim();
  return `${variable} ${word} ${value === '' ? 'vacío' : cleanMessage(value)}`;
};

// "Si {{serviceStatus}} es igual a TRAVELLING y 2 reglas más"
export const describeConditionsSentence = (conditions) => {
  const list = Array.isArray(conditions) ? conditions : [];
  const sentences = list.map((condition) => ruleSentence(condition?.rule)).filter(Boolean);
  if (!sentences.length) return '';
  const extra = sentences.length - 1;
  const joiner = list[1]?.type === 'OR' ? 'o' : 'y';
  return `Si ${sentences[0]}${extra ? ` ${joiner} ${extra} regla${extra > 1 ? 's' : ''} más` : ''}`;
};

// "Guarda en {{telefono_usuario}}, {{nombre_usuario}}"
export const describeSavedSentence = (variable) => {
  const list = Array.isArray(variable) ? variable : variable ? [variable] : [];
  const names = list.map((v) => withBraces(v?.fieldCorresp)).filter(Boolean);
  return names.length ? `Guarda en ${names.join(', ')}` : '';
};

// "GET /passenger/travels/{{codigo_usuario}}{{telefono_usuario}}/3"
export const describeRequestLine = (protocol, url) => {
  const method = protocol?.label || protocol?.id || '';
  const raw = String(url || '').trim();
  if (!raw) return method;
  const withoutHost = raw.replace(/^[a-z]+:\/\/[^/]+/i, '');
  const target = withoutHost && withoutHost !== '/'
    ? withoutHost
    : raw.replace(/^[a-z]+:\/\//i, '').replace(/\/$/, '');
  return [method, target].filter(Boolean).join(' ');
};

export const getCardContent = (node) => {
  const data = node?.data || {};
  switch (node?.type) {
    case 'basicAnswer':
      if (isStickerUrl(data.bot_response)) return 'Sticker';
      return cleanMessage(data.bot_response) || (data.file?.name ? `📎 ${data.file.name}` : '');
    case 'conditions':
      return describeConditionsSentence(data.conditions);
    case 'saveAnswer':
      return describeSavedSentence(data.variable);
    case 'queryApi':
      return describeRequestLine(data.protocol, data.url_api);
    default:
      return getNodeSummary(node);
  }
};

// El trigger "*" (cualquier mensaje) no ayuda a distinguir nodos.
export const describeTrigger = (userResponse) => {
  const trigger = String(userResponse ?? '').trim();
  return trigger && trigger !== '*' ? trigger : '';
};
